import React, { useState, useEffect } from "react";
import { useUpdateLessonMutation } from "../../services/courses";

interface Lesson {
  _id: string;
  title: string;
  description: string;
  videoPath: string;
  hasWorksheet: boolean;
  worksheetPath: string;
  duration?: number;
  order?: number;
}

interface EditLessonModalProps {
  courseId: string;
  moduleId: string;
  lesson: Lesson;
  onClose: () => void;
}

export const EditLessonModal: React.FC<EditLessonModalProps> = ({
  courseId,
  moduleId,
  lesson,
  onClose,
}) => {
  const [updateLesson, { isLoading }] = useUpdateLessonMutation();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    videoPath: "",
    hasWorksheet: false,
    worksheetPath: "",
    duration: "",
    order: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData({
      title: lesson.title,
      description: lesson.description,
      videoPath: lesson.videoPath,
      hasWorksheet: lesson.hasWorksheet,
      worksheetPath: lesson.worksheetPath,
      duration: lesson.duration !== undefined ? String(lesson.duration) : "",
      order: lesson.order !== undefined ? String(lesson.order) : "",
    });
  }, [lesson]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setHasChanges(true);

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Lesson title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Lesson title must be at least 3 characters";
    }

    if (formData.duration && isNaN(Number(formData.duration))) {
      newErrors.duration = "Duration must be a valid number (in seconds)";
    }

    if (formData.order && isNaN(Number(formData.order))) {
      newErrors.order = "Order must be a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const payload: any = {
        courseId,
        moduleId,
        lessonId: lesson._id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        videoPath: formData.videoPath.trim(),
        hasWorksheet: formData.hasWorksheet,
        worksheetPath: formData.worksheetPath.trim(),
      };

      if (formData.duration.trim()) {
        payload.duration = Number(formData.duration);
      }

      if (formData.order.trim()) {
        payload.order = Number(formData.order);
      }

      await updateLesson(payload).unwrap();

      alert("Lesson updated successfully!");
      onClose();
    } catch (err: any) {
      alert(err?.data?.error || "Failed to update lesson");
      console.error(err);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (
        hasChanges &&
        !window.confirm(
          "You have unsaved changes. Are you sure you want to close?"
        )
      ) {
        return;
      }
      onClose();
    }
  };

  const handleClose = () => {
    if (
      hasChanges &&
      !window.confirm(
        "You have unsaved changes. Are you sure you want to close?"
      )
    ) {
      return;
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Lesson</h2>
            <p className="text-sm text-gray-500 mt-1">Update lesson details</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Lesson Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.title
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="e.g., Introduction to HTML Tags"
              disabled={isLoading}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Lesson Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Brief overview of this lesson..."
              disabled={isLoading}
            />
          </div>

          <div className="mb-6">
            <label
              htmlFor="videoPath"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Video Path
            </label>
            <input
              type="text"
              id="videoPath"
              name="videoPath"
              value={formData.videoPath}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., /videos/module1/lesson1.mp4"
              disabled={isLoading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Path or URL to the video file
            </p>
          </div>

          <div className="mb-6">
            <label
              htmlFor="duration"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Duration (seconds)
            </label>
            <input
              type="number"
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.duration
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="e.g., 300 (for 5 minutes)"
              disabled={isLoading}
              min="0"
            />
            {errors.duration && (
              <p className="mt-1 text-sm text-red-600">{errors.duration}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Enter duration in seconds (e.g., 300 = 5 minutes)
            </p>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="mb-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="hasWorksheet"
                  checked={formData.hasWorksheet}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  This lesson has a worksheet
                </span>
              </label>
            </div>

            {formData.hasWorksheet && (
              <div>
                <label
                  htmlFor="worksheetPath"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Worksheet Path
                </label>
                <input
                  type="text"
                  id="worksheetPath"
                  name="worksheetPath"
                  value={formData.worksheetPath}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., /worksheets/module1/lesson1.pdf"
                  disabled={isLoading}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Path or URL to the worksheet file
                </p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="order"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Order (Optional)
            </label>
            <input
              type="number"
              id="order"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.order
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="e.g., 1"
              disabled={isLoading}
              min="0"
            />
            {errors.order && (
              <p className="mt-1 text-sm text-red-600">{errors.order}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Set the display order of this lesson. Lower numbers appear first.
            </p>
          </div>

          {hasChanges && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Unsaved changes:</strong> Remember to click "Save
                Changes" to update the lesson.
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
              disabled={isLoading || !hasChanges}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
